import {
  CampaignOutlined,
  CloseOutlined,
  DateRangeOutlined,
  ExpandMoreOutlined,
  FilterListOutlined,
  VolunteerActivismOutlined,
} from "@mui/icons-material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { type ReactNode, useCallback, useState } from "react";
import { ImpactFilterMenu } from "@/components/impactReport/ImpactFilterMenu";
import {
  buildImpactReportCampaignCountPillLabel,
  buildImpactReportCollapsedFiltersPillAriaLabel,
  buildImpactReportCollapsedFiltersPillLabel,
  buildImpactReportRemoveCampaignFilterLabel,
  buildImpactReportRemoveCauseFilterLabel,
  IMPACT_REPORT_ACTIVE_FILTERS_PANEL_ARIA_LABEL,
  IMPACT_REPORT_BAR_COMPACT_MAX_PX,
  IMPACT_REPORT_BAR_NARROW_MAX_PX,
  IMPACT_REPORT_CAMPAIGN_PILL_BODY_LABEL,
  IMPACT_REPORT_CLEAR_ALL_FALLBACK_WIDTH_PX,
  IMPACT_REPORT_CLEAR_ALL_FILTERS_LABEL,
  IMPACT_REPORT_CLEAR_CAMPAIGN_FILTER_LABEL,
  IMPACT_REPORT_DEFAULT_DATE_PRESET,
  IMPACT_REPORT_INLINE_CLEAR_ALL_HYSTERESIS_PX,
  IMPACT_REPORT_PILL_COLLAPSE_THRESHOLD,
  IMPACT_REPORT_REMOVE_DATE_FILTER_LABEL,
} from "@/constants/impactReport";
import { useImpactReportContext } from "@/context/ImpactReportContext";
import { useCampaignOptionsByIds } from "@/hooks/useCampaignOptions";
import { useElementWidth } from "@/hooks/useElementWidth";
import { useIsMobile, useMediaQuery } from "@/hooks/useMediaQuery";
import type { ImpactActiveFilterEntry, ImpactFilterPillsProps } from "@/interfaces/impactReport";
import {
  clearImpactReportControls,
  describeImpactDateSelection,
  findSingleSelectedCampaign,
} from "@/utils/impact-report-filter-helper";

/**
 * The active-filter pills, at the bar's LEFT — mirroring the prototype's `irSyncControls` pill
 * block. Every pill removes THROUGH the shared selection (`setControls`), same as the four menus
 * on the right: no pill path composes a `ReportFilter` itself, and clearing one field never
 * touches another. See `utils/impact-report-filter-helper.ts` for the one place a filter IS
 * composed, and this repo's control model for why `viewMode` cannot be reached from here even by
 * accident — nothing below reads or writes it.
 *
 * REMOVING THE LAST PILL IS NOT A SPECIAL CASE. There is no floor, no fallback selection and no
 * guard here: emptying `controls` simply returns `canRequestReport` to `false` (computed in
 * context, from the same `controls` this file writes), which sends the page back to its
 * pre-selection prompt. No request is issued for an empty filter — none ever was, because the
 * query's `enabled` flag is what gates the request, not anything here.
 *
 * ── CLEAR-ALL vs. THE COLLAPSING/SCROLLING PILLS ────────────────────────────────────────────────
 * Two SIBLINGS, not one row: the pills live in their own scrolling/collapsing region, and
 * clear-all sits OUTSIDE it whenever `hasActiveFilters` is true. It used to be the scrolling row's
 * last child, which is exactly why it could clip mid-word or scroll out of reach with no
 * affordance suggesting it existed — see the return statement below for where that split happens
 * now. Clear-all cannot shrink, so where the bar leaves too little room for it beside the pills
 * (always on mobile, and wherever `lacksRoomForClearAll` measures it so), it is not rendered here
 * at all: it moves into the collapsed pill's panel instead of overflowing onto the controls or
 * squeezing the pills until they clip.
 *
 * ── INDIVIDUAL PILLS vs. THE COLLAPSED PANEL ────────────────────────────────────────────────────
 * At or past `IMPACT_REPORT_PILL_COLLAPSE_THRESHOLD` active filters (or on any screen at or below
 * the mobile breakpoint, where even one full-width pill is tight next to four icon-only controls,
 * and wherever `lacksRoomForClearAll` measures the bar too short for clear-all beside a pill),
 * every active filter collapses into ONE pill that opens a floating panel listing them all, each
 * still individually removable. Below the threshold, pills keep rendering individually exactly as
 * before — that at-a-glance read is a pill's whole purpose. Both render paths are built from the
 * SAME `activeFilterEntries` list below, so they can never disagree about what is active, its
 * label, or how to remove it.
 */
export function ImpactFilterPills({ onOpenCampaignMenu }: ImpactFilterPillsProps) {
  const { controls, setControls, causeOptions, hasActiveFilters } = useImpactReportContext();

  const selectedCampaignIds = controls.campaignIds;
  // Only a single selection is ever named, so only that id is looked up. The page's
  // selection-empty message asks for the same ids, so react-query hashes one key and the two calls
  // share one request — a pill needs a campaign's NAME, never anything the report's own payload
  // would narrow.
  const { data: campaigns } = useCampaignOptionsByIds(
    selectedCampaignIds.length === 1 ? [...selectedCampaignIds] : [],
  );
  const singleSelectedCampaign = findSingleSelectedCampaign(selectedCampaignIds, campaigns);

  // The REQUESTED window, not the report's own `data.dateRange` (the matched bounds) — those
  // answer different questions and `ImpactReportMasthead` owns the other one. `allTime` and an
  // incomplete custom range both resolve to `null` here, and neither is an applied filter.
  const dateLabel = describeImpactDateSelection(controls.date, new Date());

  // THE SAME COMPACT BREAKPOINT `ImpactFilterMenu` USES, read through the hook rather than as CSS:
  // that file's own comment draws the line between a PAINT decision (its button collapsing to its
  // icon, which a media query alone gets right) and a STATE decision. Choosing between "render N
  // individual pills" and "render one collapsed pill with a floating panel" is the latter — the two
  // forms are structurally different subtrees, and only JS can choose between them.
  const isMobile = useIsMobile();
  // Distinct from `isMobile`: that describes the device, this describes whether the bar still has
  // room for four words beside a pill. See `IMPACT_REPORT_BAR_COMPACT_MAX_PX`.
  const isBarCompact = useMediaQuery(`(max-width: ${IMPACT_REPORT_BAR_COMPACT_MAX_PX}px)`);

  function removeCause(causeValue: string) {
    setControls((previous) => ({
      ...previous,
      cause: previous.cause.filter((value) => value !== causeValue),
    }));
  }

  function removeSingleCampaign(campaignId: string) {
    setControls((previous) => ({
      ...previous,
      campaignIds: previous.campaignIds.filter((id) => id !== campaignId),
    }));
  }

  function clearCampaigns() {
    setControls((previous) => ({ ...previous, campaignIds: [] }));
  }

  function clearDate() {
    setControls((previous) => ({
      ...previous,
      date: { preset: IMPACT_REPORT_DEFAULT_DATE_PRESET },
    }));
  }

  function clearAll() {
    // Delegated rather than composed inline as `{ cause: [], campaignIds: [], date: {...} }` here,
    // so a field added to `ImpactReportControls` later is cleared by this button without this file
    // needing to know it exists.
    setControls(clearImpactReportControls());
  }

  // ── THE UNIFIED ENTRY LIST ───────────────────────────────────────────────────────────────────
  // One descriptor per active filter, in the SAME order they render individually today (cause,
  // then campaign, then date) — built once and read by both render paths below.
  const causeEntries: ImpactActiveFilterEntry[] = controls.cause.map((causeValue) => {
    // Read off `causeOptions`, never derived from `causeValue` by casing or morphology — a
    // selection with no matching option (the menu's own list has not caught up yet) falls back to
    // the opaque value itself rather than guessing a label for it.
    const causeName =
      causeOptions.find((option) => option.causeValue === causeValue)?.causeName ?? causeValue;
    return {
      key: `cause-${causeValue}`,
      icon: <VolunteerActivismOutlined />,
      label: causeName,
      onRemove: () => removeCause(causeValue),
      removeAriaLabel: buildImpactReportRemoveCauseFilterLabel(causeName),
    };
  });

  // ONE entry either way — a single campaign's name, or the collapsed "N campaigns" group whose
  // BODY opens the campaign picker (`onActivate`) rather than removing anything. That distinction
  // survives unchanged into the floating panel — `ActiveFilterPanelRow` below is the one place
  // that reads `onActivate`.
  const campaignEntry: ImpactActiveFilterEntry | null =
    singleSelectedCampaign !== null
      ? {
          key: "campaign-single",
          icon: <CampaignOutlined />,
          label: singleSelectedCampaign.name,
          onRemove: () => removeSingleCampaign(singleSelectedCampaign.id),
          removeAriaLabel: buildImpactReportRemoveCampaignFilterLabel(singleSelectedCampaign.name),
        }
      : selectedCampaignIds.length > 1
        ? {
            key: "campaign-group",
            icon: <CampaignOutlined />,
            label: buildImpactReportCampaignCountPillLabel(selectedCampaignIds.length),
            onRemove: clearCampaigns,
            removeAriaLabel: IMPACT_REPORT_CLEAR_CAMPAIGN_FILTER_LABEL,
            onActivate: onOpenCampaignMenu,
            activateTitle: IMPACT_REPORT_CAMPAIGN_PILL_BODY_LABEL,
          }
        : null;

  const dateEntry: ImpactActiveFilterEntry | null =
    dateLabel !== null
      ? {
          key: "date",
          icon: <DateRangeOutlined />,
          label: dateLabel,
          onRemove: clearDate,
          removeAriaLabel: IMPACT_REPORT_REMOVE_DATE_FILTER_LABEL,
        }
      : null;

  const activeFilterEntries: ImpactActiveFilterEntry[] = [
    ...causeEntries,
    ...(campaignEntry !== null ? [campaignEntry] : []),
    ...(dateEntry !== null ? [dateEntry] : []),
  ];

  // ── THE MEASURED GUARD ───────────────────────────────────────────────────────────────────────
  // A viewport breakpoint only estimates how much room the bar leaves this component; these widths
  // ARE that room and what the inline form needs of it. `pillsAreaWidth` is the outer container
  // below, which the bar sizes (it grows into whatever the controls leave) rather than its own
  // content, so switching forms cannot change it. `pillsNaturalWidth` is the pills' own max-content
  // wrapper inside the scroll region, so it reads what the pills NEED however far that region is
  // squeezed. A fixed allowance sized for the small collapsed pill under-counted a campaign-group
  // pill or two individual pills, which clipped before the guard fired.
  //
  // While the guard holds the pills render collapsed, so `pillsNaturalWidth` then measures that
  // form instead. The inline form's need is therefore recorded per filter set (`inlineNeed`) while
  // it is on screen and read back while it is not; a changed filter set renders inline once to be
  // re-measured, before paint. Once guarded, the area must clear that need by
  // `IMPACT_REPORT_INLINE_CLEAR_ALL_HYSTERESIS_PX` before clear-all returns, so the bar cannot
  // flicker between forms at the edge.
  const [pillsAreaRef, pillsAreaWidth] = useElementWidth<HTMLDivElement>();
  const [pillsContentRef, pillsNaturalWidth] = useElementWidth<HTMLDivElement>();
  const [clearAllRef, measuredClearAllWidth] = useElementWidth<HTMLButtonElement>();
  // Clear-all unmounts while the guard holds, so its last real width is kept for deciding when it
  // may come back; before it has ever rendered, the fallback stands in.
  const [lastClearAllWidth, setLastClearAllWidth] = useState<number | undefined>(undefined);
  if (
    measuredClearAllWidth !== undefined &&
    measuredClearAllWidth > 0 &&
    measuredClearAllWidth !== lastClearAllWidth
  ) {
    setLastClearAllWidth(measuredClearAllWidth);
  }
  const clearAllWidth = lastClearAllWidth ?? IMPACT_REPORT_CLEAR_ALL_FALLBACK_WIDTH_PX;

  const [inlineNeed, setInlineNeed] = useState<{ key: string; width: number } | null>(null);
  const [isGuarded, setIsGuarded] = useState(false);
  // Which filter set's INLINE pills `pillsNaturalWidth` currently measures (null while the guarded
  // form is on screen). Set from the commit, not the render: the render right after the filters
  // change still holds the previous DOM's width, which must not be recorded against the new set.
  const [measuredInlineKey, setMeasuredInlineKey] = useState<string | null>(null);

  const filterSetKey = [
    isBarCompact ? "compact" : "full",
    ...activeFilterEntries.map((entry) => `${entry.key}:${entry.label}`),
  ].join("|");

  // 16 = the area's 8px gap plus clear-all's own 8px left margin.
  const measuredInlineNeedWidth =
    measuredInlineKey === filterSetKey && pillsNaturalWidth !== undefined
      ? pillsNaturalWidth + 16 + clearAllWidth
      : undefined;
  if (
    measuredInlineNeedWidth !== undefined &&
    (inlineNeed?.key !== filterSetKey || inlineNeed.width !== measuredInlineNeedWidth)
  ) {
    setInlineNeed({ key: filterSetKey, width: measuredInlineNeedWidth });
  }
  const inlineNeedWidth =
    measuredInlineNeedWidth ?? (inlineNeed?.key === filterSetKey ? inlineNeed.width : undefined);

  // Nothing recorded for this filter set yet means the inline form renders so it can be measured.
  const hysteresis = isGuarded ? IMPACT_REPORT_INLINE_CLEAR_ALL_HYSTERESIS_PX : 0;
  const lacksRoomForClearAll =
    inlineNeedWidth !== undefined &&
    pillsAreaWidth !== undefined &&
    pillsAreaWidth < inlineNeedWidth + hysteresis;
  if (lacksRoomForClearAll !== isGuarded) {
    setIsGuarded(lacksRoomForClearAll);
  }

  // Re-created whenever the form it measures changes, so React detaches and re-attaches it on that
  // commit: `useElementWidth` then re-measures synchronously, before paint, rather than waiting for
  // its observer, and `measuredInlineKey` learns which form the new width belongs to.
  const inlineFormKey = lacksRoomForClearAll ? null : filterSetKey;
  const measurePillsContent = useCallback(
    (node: HTMLDivElement | null) => {
      pillsContentRef(node);
      setMeasuredInlineKey(node === null ? null : inlineFormKey);
    },
    [pillsContentRef, inlineFormKey],
  );

  // ── THE COLLAPSE DECISION ────────────────────────────────────────────────────────────────────
  // See the file-level comment above for the full reasoning. Below the mobile breakpoint, collapse
  // at ONE rather than waiting for the threshold — matches `ImpactFilterMenu.tsx`'s own mobile
  // comment on the controls' side, where the same 375px budget is the reason they collapse to an
  // icon rather than growing another word onto the bar.
  // Collapse at the FIRST filter while the bar is compact — not just on phones. Between 768px and
  // ~1000px the controls, clear-all and two individual pills do not fit together, so waiting for
  // the threshold there is what let them collide.
  // Likewise wherever the MEASURED room is short of what the pills as rendered plus clear-all need,
  // at any viewport width: no breakpoint tracks how wide the pills actually are, and one wide
  // campaign-group pill beside clear-all was squeezed until its ✕ clipped. Collapsing here is what
  // gives clear-all a panel to move into.
  const shouldCollapsePills =
    isBarCompact || lacksRoomForClearAll
      ? activeFilterEntries.length >= 1
      : activeFilterEntries.length >= IMPACT_REPORT_PILL_COLLAPSE_THRESHOLD;

  return (
    <Box
      ref={pillsAreaRef}
      sx={{ flex: "1 1 auto", minWidth: 0, display: "flex", alignItems: "center", gap: "8px" }}
    >
      {/* ONE ROW, ALWAYS, inside its own scrolling region. Wrapping here would push a second pill
          row into a 64px fixed app bar and overlap the page content beneath it, so this region
          scrolls horizontally instead of the bar growing. Collapsing at
          `IMPACT_REPORT_PILL_COLLAPSE_THRESHOLD` keeps this region from needing to scroll in the
          first place in the common case; the scroll affordance stays as a fallback for whatever
          this region still cannot fit (a very long single cause name, for instance). The 4px
          padding/negative-margin pair keeps focus rings off the clip edge, which `overflowX: auto`
          would otherwise shave. */}
      <Box
        sx={{
          // `0 1 auto`, NOT `1 1 auto`: this region may shrink and scroll, but must never GROW
          // past its own content. Growing pins its right edge to the full available width, which
          // presses the clear-all sibling flat against the last pill — visibly overlapping it once
          // the negative margin below is taken into account.
          flex: "0 1 auto",
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          flexWrap: "nowrap",
          gap: "8px",
          overflowX: "auto",
          overflowY: "hidden",
          p: "4px",
          m: "-4px",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {/* Never shrinks with the region around it: its width is `pillsNaturalWidth`, what the
            pills need, which the region's own width stops reporting once it is squeezed. */}
        <Box
          ref={measurePillsContent}
          sx={{
            display: "inline-flex",
            flexShrink: 0,
            width: "max-content",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {shouldCollapsePills ? (
            <CollapsedFiltersPill
              entries={activeFilterEntries}
              onClearAll={isMobile || lacksRoomForClearAll ? clearAll : undefined}
            />
          ) : (
            activeFilterEntries.map((entry) =>
              entry.onActivate !== undefined ? (
                <CampaignGroupPill key={entry.key} entry={entry} />
              ) : (
                <FilterPill
                  key={entry.key}
                  icon={entry.icon}
                  label={entry.label}
                  onRemove={entry.onRemove}
                  removeAriaLabel={entry.removeAriaLabel}
                />
              ),
            )
          )}
        </Box>
      </Box>

      {/* OUTSIDE the scrolling/collapsing region above — see the file-level comment on why. If the
          guard flips while this has focus, focus is lost with it; only a live resize does that. */}
      {hasActiveFilters && !isMobile && !lacksRoomForClearAll && (
        <Box
          ref={clearAllRef}
          component="button"
          type="button"
          onClick={clearAll}
          sx={{
            border: "none",
            bgcolor: "transparent",
            p: 0,
            // The pills region's `m: "-4px"` (focus-ring bleed) eats half the parent's 8px gap, so
            // this reinstates real separation rather than sitting 4px off the last pill.
            ml: "8px",
            flexShrink: 0,
            whiteSpace: "nowrap",
            fontFamily: "var(--font-lato)",
            fontSize: "12.5px",
            fontWeight: 700,
            color: "var(--color-graphite)",
            textDecoration: "underline",
            cursor: "pointer",
            "&:hover": { color: "var(--color-rodeo-black)" },
          }}
        >
          {IMPACT_REPORT_CLEAR_ALL_FILTERS_LABEL}
        </Box>
      )}
    </Box>
  );
}

interface FilterPillProps {
  icon: ReactNode;
  label: string;
  onRemove: () => void;
  removeAriaLabel: string;
}

/** One `.ir-pill`-shaped pill: an icon, a label, and its own ✕. */
function FilterPill({ icon, label, onRemove, removeAriaLabel }: FilterPillProps) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "7px",
        py: "6px",
        pl: "14px",
        pr: "8px",
        // The row scrolls rather than wraps (see the container), so a pill must keep its intrinsic
        // width instead of being squeezed into an unreadable sliver.
        flexShrink: 0,
        bgcolor: "var(--color-light-dust)",
        border: "1px solid var(--color-rodeo-dust)",
        borderRadius: "999px",
        fontFamily: "var(--font-lato)",
        fontSize: "12.5px",
        fontWeight: 700,
        color: "var(--color-rodeo-black)",
        whiteSpace: "nowrap",
        "& .MuiSvgIcon-root": { fontSize: "15px", color: "var(--color-dark-dust)" },
      }}
    >
      {icon}
      {label}
      <PillRemoveButton onClick={onRemove} ariaLabel={removeAriaLabel} />
    </Box>
  );
}

interface CampaignGroupPillProps {
  entry: ImpactActiveFilterEntry;
}

/**
 * Two or more campaigns collapse into ONE pill — the body OPENS the campaign picker (`onActivate`);
 * the ✕ clears the whole campaign selection, not one campaign, because there is no single one left
 * to name. Behaviour unchanged from before this task; extracted into its own component so the SAME
 * entry can render standalone here or as a row inside the collapsed panel (`ActiveFilterPanelRow`)
 * without the two ever drifting apart.
 */
function CampaignGroupPill({ entry }: CampaignGroupPillProps) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "7px",
        pr: "8px",
        flexShrink: 0,
        bgcolor: "var(--color-light-dust)",
        border: "1px solid var(--color-rodeo-dust)",
        borderRadius: "999px",
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => entry.onActivate?.()}
        title={entry.activateTitle}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          py: "6px",
          pl: "14px",
          pr: "2px",
          border: "none",
          bgcolor: "transparent",
          fontFamily: "var(--font-lato)",
          fontSize: "12.5px",
          fontWeight: 700,
          color: "var(--color-rodeo-black)",
          cursor: "pointer",
          "&:hover": { color: "var(--color-dark-dust)" },
        }}
      >
        {entry.label}
        <ExpandMoreOutlined sx={{ fontSize: "16px", color: "var(--color-graphite)" }} />
      </Box>
      <PillRemoveButton onClick={entry.onRemove} ariaLabel={entry.removeAriaLabel} />
    </Box>
  );
}

interface CollapsedFiltersPillProps {
  entries: ImpactActiveFilterEntry[];
  /**
   * When supplied, clear-all renders as the last row of the floating panel instead of as a
   * standalone control beside the pill. Passed below the mobile breakpoint, where the four icon
   * controls plus the pill already consume the 375px budget, and wherever `lacksRoomForClearAll`
   * measures too little room for a separate text link beside the pill. Otherwise the standalone
   * control stays, so clear-all is reachable in one click rather than two.
   */
  onClearAll?: () => void;
}

/**
 * The ONE pill shown once `shouldCollapsePills` is true — replaces however many individual pills
 * would otherwise be active, and never grows past this single pill no matter how large the
 * underlying selection gets (the same "never grows past N pills" contract the campaign group pill
 * already keeps, extended to the whole row).
 *
 * Built on `ImpactFilterMenu` via `renderTrigger` rather than a second, hand-rolled popover: the
 * positioning, focus trap, outside-click dismissal and Escape handling are the SAME MUI `Menu`
 * every other control on this bar already relies on — see `ImpactFilterMenuProps.renderTrigger`'s
 * own comment for why this is an escape hatch on that component instead of a parallel mechanism.
 */
/**
 * Below this the collapsed pill drops its word and chevron and shows the filter icon plus the bare
 * COUNT. Measured at 375px: four 44px controls plus their 12px gaps are 212px, the drawer toggle
 * takes roughly another 48px, and the full pill is ~117px — about 393px of content in a 375px bar,
 * so the pill runs into the controls. Icon-plus-count is ~47px and clears it.
 *
 * Deliberately NARROWER than the compact breakpoint: between 400px and that threshold
 * the controls are already icon-only and the freed space is enough for the pill to keep its word,
 * which is the more readable form wherever it fits.
 */
const NARROW_MEDIA_QUERY = `@media (max-width: ${IMPACT_REPORT_BAR_NARROW_MAX_PX}px)`;

function CollapsedFiltersPill({ entries, onClearAll }: CollapsedFiltersPillProps) {
  const count = entries.length;
  const ariaLabel = buildImpactReportCollapsedFiltersPillAriaLabel(count);

  return (
    <ImpactFilterMenu
      isActive
      buttonAriaLabel={ariaLabel}
      menuMinWidth={240}
      renderTrigger={({ onOpen, isOpen, triggerAttributes }) => (
        <Box
          component="button"
          type="button"
          onClick={onOpen}
          {...triggerAttributes}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-label={ariaLabel}
          title={ariaLabel}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            py: "6px",
            pl: "14px",
            pr: "10px",
            [NARROW_MEDIA_QUERY]: { pl: "10px", pr: "10px", gap: "4px" },
            flexShrink: 0,
            border: "1px solid var(--color-rodeo-dust)",
            borderRadius: "999px",
            bgcolor: "var(--color-light-dust)",
            fontFamily: "var(--font-lato)",
            fontSize: "12.5px",
            fontWeight: 700,
            color: "var(--color-rodeo-black)",
            whiteSpace: "nowrap",
            cursor: "pointer",
            "&:hover": { color: "var(--color-dark-dust)" },
            "& .MuiSvgIcon-root": { fontSize: "15px", color: "var(--color-dark-dust)" },
          }}
        >
          <FilterListOutlined />
          {/* The bare count survives at the narrow breakpoint — the word and the chevron are what
              the bar cannot afford there. The button's `aria-label`/`title` carry the full name in
              both forms, so nothing is lost to a screen reader. */}
          <Box component="span" sx={{ [NARROW_MEDIA_QUERY]: { display: "none" } }}>
            {buildImpactReportCollapsedFiltersPillLabel(count)}
          </Box>
          <Box
            component="span"
            sx={{ display: "none", [NARROW_MEDIA_QUERY]: { display: "inline" } }}
          >
            {count}
          </Box>
          <ExpandMoreOutlined
            sx={{
              fontSize: "16px",
              color: "var(--color-graphite)",
              [NARROW_MEDIA_QUERY]: { display: "none" },
            }}
          />
        </Box>
      )}
    >
      {(closeMenu) => (
        <Box
          role="group"
          aria-label={IMPACT_REPORT_ACTIVE_FILTERS_PANEL_ARIA_LABEL}
          sx={{ display: "flex", flexDirection: "column", gap: "2px", p: "8px", minWidth: "220px" }}
        >
          {entries.map((entry) => (
            <ActiveFilterPanelRow
              key={entry.key}
              entry={entry}
              // Removing the LAST remaining entry is the one case this floating panel could go
              // empty out from under itself — closing it here is what stops that from stranding a
              // bare, contentless popover open (requirement: no empty panel left floating). Every
              // other removal just shrinks the list in place; and if the shrink also crosses back
              // below the collapse threshold, the parent stops rendering this component entirely
              // on its next render, which closes the underlying `Menu` by unmounting it.
              onRemove={
                entries.length === 1
                  ? () => {
                      entry.onRemove();
                      closeMenu();
                    }
                  : entry.onRemove
              }
              // Opening the campaign picker from inside this panel closes the panel first — two
              // floating surfaces stacked on each other is never the intent. The picker opens
              // through the CAMPAIGN control's own trigger (the context's `openCampaignMenu`
              // ref-click), which also returns focus there once the picker closes.
              onActivate={
                entry.onActivate !== undefined
                  ? () => {
                      closeMenu();
                      entry.onActivate?.();
                    }
                  : undefined
              }
            />
          ))}

          {/* Clear-all as the panel's last row (only when there is no room for it inline — see
              `onClearAll`). Closing first keeps the panel from being left open over an empty
              filter set. */}
          {onClearAll !== undefined && (
            <>
              <Divider sx={{ my: "4px" }} />
              <Box
                component="button"
                type="button"
                onClick={() => {
                  closeMenu();
                  onClearAll();
                }}
                sx={{
                  border: "none",
                  bgcolor: "transparent",
                  textAlign: "left",
                  py: "6px",
                  px: "6px",
                  borderRadius: "6px",
                  fontFamily: "var(--font-lato)",
                  fontSize: "12.5px",
                  fontWeight: 700,
                  color: "var(--color-graphite)",
                  cursor: "pointer",
                  "&:hover": { color: "var(--color-rodeo-black)" },
                }}
              >
                {IMPACT_REPORT_CLEAR_ALL_FILTERS_LABEL}
              </Box>
            </>
          )}
        </Box>
      )}
    </ImpactFilterMenu>
  );
}

interface ActiveFilterPanelRowProps {
  entry: ImpactActiveFilterEntry;
  onRemove: () => void;
  onActivate?: () => void;
}

/**
 * One row inside the collapsed pill's floating panel: an icon, the same label a standalone pill
 * would show, and its own ✕ — every active filter stays individually removable from in here. The
 * campaign group's row additionally makes its LABEL clickable, opening the campaign picker exactly as
 * its standalone pill's body does (`CampaignGroupPill`) — the two surfaces agree because both read
 * `onActivate` off the same `ImpactActiveFilterEntry`.
 */
function ActiveFilterPanelRow({ entry, onRemove, onActivate }: ActiveFilterPanelRowProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        py: "6px",
        px: "6px",
        borderRadius: "6px",
        "& .MuiSvgIcon-root": { fontSize: "16px", color: "var(--color-dark-dust)" },
      }}
    >
      {entry.icon}
      {onActivate !== undefined ? (
        <Box
          component="button"
          type="button"
          onClick={onActivate}
          title={entry.activateTitle}
          sx={{
            flex: 1,
            textAlign: "left",
            border: "none",
            bgcolor: "transparent",
            p: 0,
            fontFamily: "var(--font-lato)",
            fontSize: "13px",
            fontWeight: 700,
            color: "var(--color-rodeo-black)",
            cursor: "pointer",
            "&:hover": { color: "var(--color-dark-dust)" },
          }}
        >
          {entry.label}
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            fontFamily: "var(--font-lato)",
            fontSize: "13px",
            fontWeight: 700,
            color: "var(--color-rodeo-black)",
          }}
        >
          {entry.label}
        </Box>
      )}
      <PillRemoveButton onClick={onRemove} ariaLabel={entry.removeAriaLabel} />
    </Box>
  );
}

interface PillRemoveButtonProps {
  onClick: () => void;
  ariaLabel: string;
}

/** The `.ir-pill-x` circle, shared by every pill (including the collapsed panel's rows). */
function PillRemoveButton({ onClick, ariaLabel }: PillRemoveButtonProps) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={ariaLabel}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "20px",
        height: "20px",
        border: "none",
        borderRadius: "50%",
        bgcolor: "transparent",
        p: 0,
        color: "var(--color-graphite)",
        cursor: "pointer",
        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.08)", color: "var(--color-rodeo-black)" },
        "& .MuiSvgIcon-root": { fontSize: "15px" },
      }}
    >
      <CloseOutlined />
    </Box>
  );
}
